import { useState, useEffect, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Icon from 'src/features/common/CustomIcon';
import Link from '@mui/material/Link';
import RefreshTwoToneIcon from '@mui/icons-material/RefreshTwoTone';
import Tooltip from '@mui/material/Tooltip';
import {
    ActivityDialogStore,
    DeleteLeadStore,
    InboundCallStore,
    Lead,
    LeadScreenStore,
    ViewType,
} from 'src/stores/LeadStore';
import { ActivityColor, colorDateTooltipOverdueDashboard, nextDateDaysDashboard } from 'src/utils/date-utils';
import IconButton from '@mui/material/IconButton';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';
import { Filter } from '../../filters/FilterTypes';
import OverrideMenu from '../../Dashboard/OverrideMenu';
import RenderIf, { ClaimType, ClaimValue } from '../../common/RenderIf';
import { useGetInfiniteLeads } from '../../../hooks/lead-workview-hooks';
import { GridColDef } from '@mui/x-data-grid-pro';
import LeadDataGridView from '../../Components/LeadDataGridView';
import { LeadGridItem } from '../../../types/Lead';
import { useQueryClient } from '@tanstack/react-query';
import { client } from '../../../utils/axios-client';
import Grid from '@mui/material/Grid';
import LeadSummaryView from '../view/LeadSummaryView';
import NoMoreLeadsDialog from '../../../v2/features/Leads/NoMoreLeadsDialog/NoMoreLeadsDialog';

interface CellType {
    row: LeadGridItem;
}

type Props = {
    searchTerm: string;
    filter: Filter;
    children?: ReactNode;
};

const LeadGridView = ({ searchTerm, filter, children }: Props) => {
    const [loaded, setLoaded] = useState(false);
    const [pageSize, setPageSize] = useState(50);
    const selectedLeadCard = LeadScreenStore.useState((s) => s.selectedLeadCard);
    const loadNextLead = LeadScreenStore.useState((s) => s.loadNextLead);
    const loadPreviousLead = LeadScreenStore.useState((s) => s.loadPreviousLead);
    const editMode = LeadScreenStore.useState((s) => s.editMode);
    const refreshLeads = LeadScreenStore.useState((s) => s.refreshLeads);
    const selectedLead = LeadScreenStore.useState((s) => s.selectedLead);
    const fetchedNext = LeadScreenStore.useState((s) => s.fetchedNext);
    const closeOnNoMoreLeads = LeadScreenStore.useState((s) => s.closeOnNoMoreLeads);

    const [noMoreLeadsOpen, setNoMoreLeadsOpen] = useState(false);

    const {
        data: paginatedLeads,
        isLoading,
        fetchNextPage,
        hasNextPage,
        refetch,
    } = useGetInfiniteLeads(filter, searchTerm, pageSize);
    const queryClient = useQueryClient();

    useEffect(() => {
        if (loadNextLead || loadPreviousLead) {
            loadNextLeadAsync();
        }
    }, [loadNextLead, loadPreviousLead]);

    useEffect(() => {
        debugger;
        if (loadNextLead) {
            processNextLead();
        } else if (loadPreviousLead) {
            processPreviousLead();
        }
    }, [paginatedLeads?.pages, loadNextLead, loadPreviousLead]);

    useEffect(() => {
        if (!editMode || refreshLeads) {
            refetch();

            if (selectedLeadCard) {
                showLeadCard({ id: selectedLeadCard.id } as LeadGridItem);
            }

            LeadScreenStore.update((s) => {
                s.refreshLeads = false;
            });
        }
    }, [editMode, refreshLeads]);

    const viewEditLead = async (lead: LeadGridItem) => {
        const leadData = await queryClient.fetchQuery<Lead>({
            queryKey: ['leads-list-loaded-lead', lead.id],
            queryFn: () => {
                return client.get(`/api/leads/get/${lead.id}`).then((res) => res.data);
            },
        });

        ActivityDialogStore.update((s) => {
            s.editLeadMode = true;
        });
        LeadScreenStore.update((s) => {
            s.selectedLead = leadData;
            s.selectedView = ViewType.Edit;
            s.editMode = true;
        });
    };

    const showLeadCard = async (lead: LeadGridItem) => {
        // Invalidate the cached query before fetching
        await queryClient.invalidateQueries(['leads-list-loaded-lead', lead.id]);

        const leadData = await queryClient.fetchQuery<Lead>({
            queryKey: ['leads-list-loaded-lead', lead.id],
            queryFn: () => {
                return client.get(`/api/leads/get/${lead.id}`).then((res) => res.data);
            },
        });

        LeadScreenStore.update((s) => {
            s.selectedLeadCard = leadData;
        });
    };

    const loadNextLeadAsync = async () => {
        await refetch();
    };

    //const overrideLead = (lead: LeadGridItem) => {
    //    ActivityDialogStore.update(s => { s.editLeadMode = true });
    //    LeadScreenStore.update(s => { s.selectedLead = lead; s.selectedView = ViewType.Edit; s.editMode = true })
    //};

    const onExecuteActivity = async (lead: LeadGridItem) => {
        const leadData = await queryClient.fetchQuery<Lead>({
            queryKey: ['leads-list-loaded-lead', lead.id],
            queryFn: () => {
                return client.get(`/api/leads/get/${lead.id}`).then((res) => res.data);
            },
        });
        InboundCallStore.update((s) => {
            s.showPhoneControls = false;
        });
        LeadScreenStore.update((s) => {
            s.selectedLead = leadData;
            s.selectedView = ViewType.DoActivity;
            s.editMode = false;
            s.loadNextLead = false;
            s.loadPreviousLead = false;
            s.fetchedNext = false;
            s.closeOnNoMoreLeads = false;
        });
    };

    const setActionButton = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();

        const timeDifference = Math.floor(date.getTime() - now.getTime());
        let dayDifference = timeDifference / (1000 * 3600 * 24);

        dayDifference = Math.round(dayDifference);

        if (dayDifference > 1) {
            return true;
        } else if (dayDifference == 1) {
            return true;
        }
        return false;
    };

    const onDeleteLead = async (lead: LeadGridItem) => {
        const leadData = await queryClient.fetchQuery<Lead>({
            queryKey: ['leads-list-loaded-lead', lead.id],
            queryFn: () => {
                return client.get(`/api/leads/get/${lead.id}`).then((res) => res.data);
            },
        });

        LeadScreenStore.update((s) => {
            (s.selectedLead = leadData), (s.selectedView = ViewType.None);
        });
        DeleteLeadStore.update((s) => {
            s.deleteLead = true;
        });
    };

    const processPreviousLead = async () => {
        if (!loadPreviousLead || !paginatedLeads || !selectedLead) return;
        debugger;
        if (paginatedLeads.pages.length > 0 && paginatedLeads.pages[0].items.length > 0) {
            for (let pageIndex = 0; pageIndex <= paginatedLeads.pages.length; pageIndex++) {
                const leadIndex = paginatedLeads.pages[pageIndex].items.findIndex((s) => s.id == selectedLead.id);

                if (leadIndex > -1) {
                    if (paginatedLeads.pages[pageIndex].items[0].id == selectedLead.id && pageIndex === 0) {
                        setNoMoreLeadsOpen(true);

                        LeadScreenStore.update((s) => {
                            s.loadNextLead = false;
                            s.loadPreviousLead = false;
                        });
                    } else if (paginatedLeads.pages[pageIndex].items[0].id == selectedLead.id) {
                        // Execute last lead on the previous page
                        onExecuteActivity(
                            paginatedLeads.pages[pageIndex - 1].items[paginatedLeads.pages[pageIndex - 1].items.length],
                        );
                    } else {
                        // Execute previous lead on the current page
                        onExecuteActivity(paginatedLeads.pages[pageIndex].items[leadIndex - 1]);
                    }
                }
            }
        }
    };

    const processNextLead = async () => {
        if (!loadNextLead || !paginatedLeads || !selectedLead) return;
        debugger;
        if (paginatedLeads.pages.length > 0 && paginatedLeads.pages[0].items.length > 0) {
            for (let pageIndex = 0; pageIndex <= paginatedLeads.pages.length; pageIndex++) {
                const leadIndex = paginatedLeads.pages[pageIndex].items.findIndex((s) => s.id == selectedLead.id);

                if (leadIndex > -1) {
                    if (paginatedLeads.pages[pageIndex].items.length - 1 == leadIndex) {
                        if (fetchedNext) {
                            setNoMoreLeadsOpen(true);

                            LeadScreenStore.update((s) => {
                                s.loadNextLead = false;
                                s.loadPreviousLead = false;
                                s.fetchedNext = false;
                            });

                            return;
                        }

                        // next page.
                        const result = await fetchNextPage();
                        if (result.isError) {
                            if (closeOnNoMoreLeads) {
                                LeadScreenStore.update((s) => {
                                    s.loadNextLead = false;
                                    s.loadPreviousLead = false;
                                    s.fetchedNext = false;
                                    s.closeOnNoMoreLeads = false;
                                    s.selectedLead = null;
                                    s.selectedView = ViewType.None;
                                });

                                refetch();
                            } else {
                                setNoMoreLeadsOpen(true);

                                LeadScreenStore.update((s) => {
                                    s.loadNextLead = false;
                                    s.loadPreviousLead = false;
                                    s.fetchedNext = false;
                                    s.closeOnNoMoreLeads = false;
                                });
                            }
                        } else {
                            LeadScreenStore.update((s) => {
                                s.fetchedNext = true;
                            });
                        }

                        return;
                    } else {
                        onExecuteActivity(paginatedLeads.pages[0].items[leadIndex + 1]);
                        return;
                    }
                }
            }
        }

        setNoMoreLeadsOpen(true);

        LeadScreenStore.update((s) => {
            s.loadNextLead = false;
            s.loadPreviousLead = false;
            s.fetchedNext = false;
        });
    };

    const preColumns = [
        {
            flex: 0.1,
            minWidth: 130,
            field: 'DueDate',
            headerName: 'Due Date',
            hideable: false,
            valueGetter: ({ row }: CellType) => row.nextActivityDateTime,
            renderCell: ({ row }: CellType) => {
                return (
                    <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'left' }}>
                            <Typography
                                variant="caption"
                                sx={{ color: colorDateTooltipOverdueDashboard(row.nextActivityDateTime) }}
                            >
                                {nextDateDaysDashboard(row.nextActivityDateTime)}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
    ] as GridColDef[];

    const postColumns = [
        {
            flex: 0.3,
            minWidth: 100,
            field: 'Edit',
            headerName: 'Edit',
            hideable: true,
            valueGetter: ({ row }: CellType) => row.id,
            renderCell: ({ row }: CellType) => {
                return (
                    <RenderIf claimType={ClaimType.Leads} claimValue={ClaimValue.Edit}>
                        <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'left' }}>
                                <Link
                                    sx={{ color: 'text.primary', textDecoration: 'none' }}
                                    underline="hover"
                                    component="button"
                                    variant="caption"
                                    onClick={() => viewEditLead(row)}
                                >
                                    Edit
                                </Link>
                            </Box>
                        </Box>
                    </RenderIf>
                );
            },
        },
        {
            flex: 0.3,
            minWidth: 100,
            field: 'Delete',
            headerName: 'Delete',
            hideable: true,
            valueGetter: ({ row }: CellType) => row.id,
            renderCell: ({ row }: CellType) => {
                return (
                    <RenderIf claimType={ClaimType.Leads} claimValue={ClaimValue.Delete}>
                        <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                            <IconButton aria-label="Tasks" color="error" onClick={() => onDeleteLead(row)}>
                                <DeleteTwoToneIcon />
                            </IconButton>
                        </Box>
                    </RenderIf>
                );
            },
        },
        {
            flex: 0.3,
            minWidth: 100,
            field: 'Override',
            headerName: 'Override',
            hideable: true,
            valueGetter: ({ row }: CellType) => row.id,
            renderCell: ({ row }: CellType) => {
                return (
                    <RenderIf claimType={ClaimType.Leads} claimValue={ClaimValue.OverrideWorkflow}>
                        <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'left' }}>
                                <OverrideMenu row={row} />
                            </Box>
                        </Box>
                    </RenderIf>
                );
            },
        },
        {
            flex: 0.3,
            minWidth: 100,
            field: 'Action',
            headerName: 'Action',
            hideable: false,
            valueGetter: ({ row }: CellType) => row.nextActivityId,
            renderCell: ({ row }: CellType) => {
                return (
                    <RenderIf claimType={ClaimType.Leads} claimValue={ClaimValue.ExecuteActivities}>
                        <Box sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'left' }}>
                                <IconButton
                                    aria-label="Next activity"
                                    onClick={() => onExecuteActivity(row)}
                                    disabled={setActionButton(row.nextActivityDateTime)}
                                >
                                    <Icon
                                        icon={row.nextActivityIcon}
                                        sx={{ color: ActivityColor(row.nextActivityDateTime) }}
                                    ></Icon>
                                </IconButton>
                            </Box>
                        </Box>
                    </RenderIf>
                );
            },
        },
    ] as GridColDef[];

    return (
        <Grid container spacing={2}>
            <Grid item xs={selectedLeadCard ? 8 : 12}>
                <Card sx={{ width: '100%' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', p: 5 }}>
                            {children}
                            <Box>
                                <Tooltip title="Refresh">
                                    <IconButton aria-label="Next activity" onClick={() => refetch()}>
                                        <RefreshTwoToneIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                    <LeadDataGridView
                        isLoading={isLoading}
                        leadPages={paginatedLeads?.pages}
                        selectedLeadIds={[]}
                        hasNextPage={hasNextPage === true}
                        fetchNextPage={fetchNextPage}
                        preColumns={preColumns}
                        postColumns={postColumns}
                        onSelectionModeChange={() => {}}
                        allowLeadQuickView={true}
                        allowViewCadenceSteps={true}
                        onLeadClick={showLeadCard}
                    />
                </Card>
            </Grid>
            {selectedLeadCard && (
                <Grid item xs={4}>
                    <LeadSummaryView
                        item={selectedLeadCard}
                        onClose={() => {
                            LeadScreenStore.update((s) => {
                                s.selectedLeadCard = null;
                            });
                        }}
                    />
                </Grid>
            )}

            <NoMoreLeadsDialog open={noMoreLeadsOpen} onClose={() => setNoMoreLeadsOpen(false)} />
        </Grid>
    );
};

export { LeadGridView };
export default LeadGridView;
